import { useRef, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { AgentApiProfitsItem } from '@/types/serializers';
import clsx from 'clsx';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';

type ProfitAction =
  | 'sendInvoice'
  | 'confirmEntry'
  | 'payment'
  | 'refund'
  | 'refunded'
  | 'candidateDecline'
  | 'companyDecline'
  | 'delete';

type Props = {
  profit: AgentApiProfitsItem;
  onAction: (profit: AgentApiProfitsItem, action: ProfitAction) => void;
};

export type { ProfitAction };

export default function ProfitStatusMenu({ profit, onAction }: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null);
  const [isMenuPositionResolved, setIsMenuPositionResolved] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const actions = useMemo(() => {
    const items: { key: ProfitAction; label: string; color: 'default' | 'primary' | 'danger' }[] =
      [];
    switch (profit.status) {
      case 'waiting_entry':
        items.push({ key: 'sendInvoice', label: '請求書送付', color: 'primary' });
        items.push({ key: 'confirmEntry', label: '入社確定', color: 'primary' });
        items.push({ key: 'candidateDecline', label: '候補者辞退', color: 'danger' });
        items.push({ key: 'companyDecline', label: '企業NG', color: 'danger' });
        if (profit.deletable) items.push({ key: 'delete', label: '削除', color: 'danger' });
        break;
      case 'invoice_sent':
        items.push({ key: 'confirmEntry', label: '入社確定', color: 'primary' });
        items.push({ key: 'candidateDecline', label: '候補者辞退', color: 'danger' });
        items.push({ key: 'companyDecline', label: '企業NG', color: 'danger' });
        if (profit.deletable) items.push({ key: 'delete', label: '削除', color: 'danger' });
        break;
      case 'waiting_payment':
        items.push({ key: 'payment', label: '入金確定', color: 'primary' });
        items.push({ key: 'candidateDecline', label: '候補者辞退', color: 'danger' });
        items.push({ key: 'companyDecline', label: '企業NG', color: 'danger' });
        break;
      case 'paid':
        items.push({ key: 'refund', label: '返金処理', color: 'default' });
        break;
      case 'refund':
        items.push({ key: 'refunded', label: '返金確定', color: 'danger' });
        break;
    }
    return items;
  }, [profit.status, profit.deletable]);

  const isDisabled = actions.length === 0;

  useEffect(() => {
    if (isMenuOpen && buttonRef.current) {
      setMenuRect(buttonRef.current.getBoundingClientRect());
      setMenuPosition('bottom');
      setIsMenuPositionResolved(false);
      return;
    }
    setMenuRect(null);
    setIsMenuPositionResolved(false);
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen || !menuRect || !menuRef.current) return;

    const viewportHeight = window.innerHeight;
    const menuHeight = menuRef.current.offsetHeight;
    const bottomSpace = viewportHeight - menuRect.bottom;
    const topSpace = menuRect.top;

    if (bottomSpace < menuHeight && topSpace > menuHeight) {
      setMenuPosition('top');
    } else {
      setMenuPosition('bottom');
    }

    setIsMenuPositionResolved(true);
  }, [isMenuOpen, menuRect, actions.length]);

  // メニュー外クリックで閉じる
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isMenuOpen]);

  const colorClass = {
    default: 'text-main',
    primary: 'text-primary',
    danger: 'text-danger',
  };

  return (
    <div className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        className={clsx(
          'flex h-[42px] w-full appearance-none items-center rounded border border-solid border-border px-3',
          isDisabled && 'cursor-not-allowed opacity-60'
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (isDisabled) return;
          setIsMenuOpen((prev) => !prev);
        }}
      >
        <p className="text-xs text-nowrap">{profit.status_title}</p>
        {!isDisabled &&
          (isMenuOpen ? (
            <LuChevronUp className="ml-auto size-5 text-[#262626]" />
          ) : (
            <LuChevronDown className="ml-auto size-5 text-[#262626]" />
          ))}
      </button>
      {isMenuOpen &&
        menuRect &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] w-[180px] rounded bg-white shadow-[0px_0px_12px_0px_#00000033]"
            style={{
              visibility: isMenuPositionResolved ? 'visible' : 'hidden',
              left: `${menuRect.left}px`,
              top: menuPosition === 'bottom' ? `${menuRect.bottom + 4}px` : undefined,
              bottom:
                menuPosition === 'top' ? `${window.innerHeight - menuRect.top + 4}px` : undefined,
            }}
          >
            {actions.map((action) => (
              <div
                key={action.key}
                className={clsx(
                  'flex h-xxl w-full cursor-pointer items-center px-3 text-sm hover:opacity-60',
                  colorClass[action.color]
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onAction(profit, action.key);
                  setIsMenuOpen(false);
                }}
              >
                {action.label}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
